import React, { useRef, useState } from 'react';
import { useI18n } from '../../i18n';
import useModalEscape from './useModalEscape';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function truncate(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + '…').width > maxWidth) t = t.slice(0, -1);
  return t + '…';
}

async function fetchBlob(url) {
  const res = await fetch(url, { cache: 'no-cache', mode: 'cors' });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

export default function TryOnShareCard({ item, formatPrice, formatSelectedTotal, onClose }) {
  const { t } = useI18n();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  useModalEscape(true, onClose);

  const hasProducts = item.try_on_type === 'selected_products' && item.selected_products?.length > 0;
  const celebrity = item.event?.celebrity_name || 'Outfit Detective';
  const products = hasProducts ? item.selected_products.slice(0, 4) : [];

  async function downloadCard() {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(false);

    const W = 1080;
    const PANEL_H = hasProducts ? 200 + products.length * 54 + 72 : 200;
    const IMG_H = W * 5 / 4 - PANEL_H; // full card = 1080×1350

    try {
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = W * 5 / 4; // 1350
      const ctx = canvas.getContext('2d');

      // Background
      ctx.fillStyle = '#10131a';
      ctx.fillRect(0, 0, W, canvas.height);

      // Try-on image (contain, centered)
      const imgObjUrl = await fetchBlob(item.image_url);
      try {
        const img = await loadImage(imgObjUrl);
        const scale = Math.min(W / img.width, IMG_H / img.height);
        const iw = img.width * scale;
        const ih = img.height * scale;
        ctx.drawImage(img, (W - iw) / 2, (IMG_H - ih) / 2, iw, ih);
      } finally {
        URL.revokeObjectURL(imgObjUrl);
      }

      // Panel separator
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, IMG_H);
      ctx.lineTo(W, IMG_H);
      ctx.stroke();

      await document.fonts.ready;

      const PAD = 64;
      let y = IMG_H + 52;

      // Logo circle
      const LOGO = 44;
      const logoObjUrl = await fetchBlob('/brand/outfit-detective-mark-512.png');
      try {
        const logo = await loadImage(logoObjUrl);
        ctx.save();
        ctx.beginPath();
        ctx.arc(PAD + LOGO / 2, y, LOGO / 2, 0, Math.PI * 2);
        ctx.fillStyle = '#15151a';
        ctx.fill();
        ctx.clip();
        const ls = (LOGO * 0.86) / Math.max(logo.width, logo.height);
        ctx.drawImage(logo, PAD + (LOGO - logo.width * ls) / 2, y - (logo.height * ls) / 2, logo.width * ls, logo.height * ls);
        ctx.restore();
      } finally {
        URL.revokeObjectURL(logoObjUrl);
      }

      // Brand label
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '600 26px system-ui,-apple-system,sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText('OUTFIT DETECTIVE', PAD + LOGO + 18, y);

      y += LOGO / 2 + 44;

      // Celebrity name
      ctx.fillStyle = '#ff2d7a';
      ctx.font = '800 50px system-ui,-apple-system,sans-serif';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(truncate(ctx, celebrity.toUpperCase(), W - PAD * 2), PAD, y);

      y += 14;

      if (hasProducts) {
        // Top rule
        ctx.strokeStyle = 'rgba(255,255,255,0.1)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke();
        y += 42;

        for (const p of products) {
          const priceStr = formatPrice(p);
          const store = p.store || p.brand || '';

          // Price (right-aligned)
          ctx.fillStyle = 'rgba(255,255,255,0.85)';
          ctx.font = '700 28px system-ui,-apple-system,sans-serif';
          const pw = ctx.measureText(priceStr).width;
          ctx.fillText(priceStr, W - PAD - pw, y);

          // Store (before price)
          ctx.fillStyle = 'rgba(255,255,255,0.38)';
          ctx.font = '400 22px system-ui,-apple-system,sans-serif';
          const sw = ctx.measureText(store).width;
          ctx.fillText(store, W - PAD - pw - sw - 18, y);

          // Product name (left, truncated to avoid overlap)
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.font = '600 28px system-ui,-apple-system,sans-serif';
          const maxNW = W - PAD * 2 - pw - sw - 36;
          ctx.fillText(truncate(ctx, p.name, maxNW), PAD, y);

          y += 54;
        }

        // Bottom rule
        ctx.strokeStyle = 'rgba(255,255,255,0.1)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(PAD, y - 8); ctx.lineTo(W - PAD, y - 8); ctx.stroke();
        y += 24;

        // Total
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = '500 24px system-ui,-apple-system,sans-serif';
        ctx.letterSpacing = '2px';
        ctx.fillText('TOTAL', PAD, y);
        ctx.letterSpacing = '0px';
        const totalStr = formatSelectedTotal(item);
        ctx.fillStyle = '#ffffff';
        ctx.font = '800 38px system-ui,-apple-system,sans-serif';
        const tw = ctx.measureText(totalStr).width;
        ctx.fillText(totalStr, W - PAD - tw, y);
      }

      // URL bottom-right
      ctx.fillStyle = 'rgba(255,255,255,0.22)';
      ctx.font = '400 22px system-ui,-apple-system,sans-serif';
      const urlStr = 'outfitdetective.com';
      const uw = ctx.measureText(urlStr).width;
      ctx.fillText(urlStr, W - PAD - uw, canvas.height - 38);

      // Download
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `outfit-detective-${celebrity.replace(/\s+/g, '-').toLowerCase()}.png`;
        link.click();
      }, 'image/png');

    } catch (err) {
      console.error('Share card generation failed', err);
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="ct-share-card-overlay" onClick={onClose}>
      <div className="ct-share-card-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="ct-share-card-sheet-header">
          <span>{t('shareCard.title')}</span>
          <button type="button" className="ct-share-card-sheet-close" onClick={onClose} aria-label={t('aiDialog.close')}>
            <i className="ph ph-x"></i>
          </button>
        </div>

        <div className="ct-share-card-scroll">
          <div className="ct-share-card" ref={cardRef}>
            <div className="ct-share-card-photo">
              <img src={item.image_url} alt="" />
            </div>
            <div className="ct-share-card-panel">
              <div className="ct-share-card-brand-row">
                <span className="ct-brand-mark image ct-share-card-logo">
                  <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
                </span>
                <span className="ct-share-card-brand-name">Outfit Detective</span>
              </div>
              <p className="ct-share-card-celeb">{celebrity}</p>
              {hasProducts ? (
                <>
                  <ul className="ct-share-card-products">
                    {products.map((p) => (
                      <li key={p.candidate_id}>
                        <span className="ct-share-card-product-name">{p.name}</span>
                        <span className="ct-share-card-product-store">{p.store || p.brand}</span>
                        <em className="ct-share-card-product-price">{formatPrice(p)}</em>
                      </li>
                    ))}
                  </ul>
                  <div className="ct-share-card-total">
                    <span>Total</span>
                    <strong>{formatSelectedTotal(item)}</strong>
                  </div>
                </>
              ) : null}
              <p className="ct-share-card-url">outfitdetective.com</p>
            </div>
          </div>

          <div className="ct-share-card-actions">
            {downloadError ? (
              <p className="ct-share-card-error">{t('shareCard.downloadError')}</p>
            ) : null}
            <button
              type="button"
              className="btn btn-dark ct-share-card-download"
              onClick={downloadCard}
              disabled={downloading}
            >
              <i className="ph ph-download-simple"></i>
              {downloading ? t('shareCard.generating') : t('shareCard.download')}
            </button>
            <p className="ct-share-card-hint">{t('shareCard.hint')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
